<template>
    <div>
        <p v-if="title" class="subheading" :class="{'mb-16': !subtitle}">{{ title }}</p>
        <p v-if="subtitle" class="subheading-description">{{ subtitle }}</p>
        <div v-if="displayThumbnail" class="mb-20">
            <cdn-image :src="displayThumbnail" :size="{height: height, ratio: displayRatio}" class="rounded border" cover/>
        </div>
        <missing-info v-else :icon="icons.image" :text="$t('missing.image') + (helpSize ? `<br>${$t('help.image.optimal_format', {size: helpSize, ratio: helpRatio}, `Optimale Größe: ${helpSize} (Verhältnis: ${helpRatio})`)}` : '')"/>

        <wrapper class="mb-20" left grow>
            <btn v-if="displayThumbnail && !displayThumbnail.is_default" @click="removeImage" color="warning" :disabled="disabled" outlined>
                <Icon :icon="icons.remove"/>
                <span>{{ $t('action.remove_image') }}</span>
            </btn>
            <btn @click="showModal = true" :disabled="disabled">
                <Icon :icon="icons.upload"/>
                <span>{{ displayThumbnail ? $t('action.edit_image') : $t('action.upload_image') }}</span>
            </btn>
        </wrapper>

        <image-cropper-modal v-model="showModal" :thumbnail="displayThumbnail" :src="displayFile"
                             :help-size="helpSize"
                             :help-ratio="helpRatio"
							 :existing="existing"
                             :ratio="ratio" @saved="handleUpdate"/>
    </div>
</template>

<script>
import FormInputsMixin from "../FormInputsMixin";
import {EXISTING_FORM_INPUT_VALUE, imageIcon, removeIcon, uploadIcon} from "../../../config";

export default {

    name: 'AttachCroppedImageFormInputs',

    mixins: [FormInputsMixin],

    props: {
        // The name of the data prop to store the image
        prop: {
            type: String,
            default: 'avatar'
        },

        // Whether to store the cropped image within a '[prop]_thumbnail' prop on the data and submit the uncropped image as the original prop
        // Additionally the value of '[prop]_thumbnail' will be displayed as the thumbnail on the form
        withThumbnail: {
            type: Boolean,
            default: false
        },

        title: {
            type: String,
            default: null
        },

        subtitle: {
            type: String,
            default: null
        },

        // The fixed ratio to use for cropping
        ratio: {
            type: [Number, String],
            default: null
        },

        // The height of the displayed image
        height: {
            type: [Number, String],
            default: 200
        },

        // The optimal size of the image to upload to show within a help text to the user
        helpSize: {
            type: String,
            default: null
        },

        // The ratio of the image to show as a help text
        helpRatio: {
            type: String,
            default: '1/1'
        },

		// A set of images that can be selected from the gallery
		existing: {
			type: Array,
			default: () => [],
		}
    },

    data() {
        return {
            showModal: false,
            displayThumbnail: null,
            displaySource: null,
            displayFile: null,

            // Whether the data is modified by this component
            // Required to refresh the image data props when the data is changed outside the component
            selfUpdating: false,

            imageWatcher: null,

			icons: {
				remove: removeIcon,
				upload: uploadIcon,
				image: imageIcon,
			}
        }
    },

    computed: {
        displayRatio() {
            if (this.ratio) {
                return this.ratio;
            }
            if (this.withThumbnail && this.data[`${this.prop}_thumbnail_ratio`]) {
                return this.data[`${this.prop}_thumbnail_ratio`];
            }
            if (this.data[`${this.prop}_ratio`]) {
                return this.data[`${this.prop}_ratio`];
            }
            return 1;
        }
    },

    watch: {
        prop: {
            handler: function() {
                if (this.imageWatcher) {
                    this.imageWatcher();
                }
                // Dynamic watcher required to only watch our image prop
                this.imageWatcher = this.$watch(this.prop, () => {
                    if (!this.selfUpdating) {
                        // We do not want to show default images as the selected thumbnail
                        this.displayThumbnail = this.withThumbnail ? this.data[`${this.prop}_thumbnail`] : this.data[this.prop];
                        this.displaySource = this.data[this.prop];
                        this.displayFile = this.data[this.prop];

                        // We saved the data in the 'displayThumbnail' and 'displaySource' data props and delete them now
                        // to only submit the data props when the image was changed
						if (this.data[this.prop]) {
							this.data[this.prop] = EXISTING_FORM_INPUT_VALUE;
						}
						if (this.withThumbnail && this.data[`${this.prop}_thumbnail`]) {
							this.data[`${this.prop}_thumbnail`] = EXISTING_FORM_INPUT_VALUE;
						}
                    }
                    this.selfUpdating = false;
                }, {immediate: true} )
            },
            immediate: true
        }
    },

    beforeUnmount() {
        if (this.imageWatcher) {
            this.imageWatcher();
        }
    },

    methods: {

        handleUpdate(image, cropData, existing, originalPreview, croppedPreview) {
            this.selfUpdating = true;
            this.displayThumbnail = croppedPreview;
            this.displaySource = this.withThumbnail ? originalPreview : croppedPreview ;
            this.displayFile = image;

			// If no crop data is given, we use the original image
           if (cropData) {
			   if (this.withThumbnail) {
				   this.data[`${this.prop}_thumbnail_data`] = JSON.stringify(cropData);
			   } else {
				   this.data[`${this.prop}_data`] = JSON.stringify(cropData);
			   }

			   // We need to update the ratio prop if not explicitly set, so we can display the preview correctly
			   if (!this.ratio) {
				   if (this.withThumbnail) {
					   this.data[`${this.prop}_thumbnail_ratio`] = cropData.width / cropData.height;
				   } else {
					   this.data[`${this.prop}_ratio`] = cropData.width / cropData.height;
				   }
			   }
		   }

            this.showModal = false;

            if (image && !existing) {
                // A new image was uploaded
                this.data[this.prop] = image;
                this.data[`${this.prop}_selected`] = undefined;

                if (this.withThumbnail) {
                    this.data[`${this.prop}_thumbnail`] = image;
                    this.data[`${this.prop}_thumbnail_selected`] = undefined;
                }
            } else {
				// The form inputs might be used to select an image from the gallery as avatar thumbnail but not as avatar, e.g., event avatar.
				// We do not want to modify / crop these gallery images and only want to set them as thumbnail.
				const hasSelectedGalleryImageWithoutCropping = this.withThumbnail && existing && !cropData;
				if (hasSelectedGalleryImageWithoutCropping) {
					this.data[`${this.prop}_selected`] = undefined;
					this.data[`${this.prop}_data`] = undefined;
					this.data[this.prop] = null; // Delete the existing avatar prop

					this.data[`${this.prop}_thumbnail`] = undefined;
					this.data[`${this.prop}_thumbnail_data`] = undefined;
					this.data[`${this.prop}_thumbnail_selected`] = image;
				} else {
					// The existing image was modified, e.g., cropped
					this.data[`${this.prop}_selected`] = image;
					this.data[this.prop] = undefined;

					if (this.withThumbnail) {
						this.data[`${this.prop}_thumbnail`] = undefined;
						this.data[`${this.prop}_thumbnail_selected`] = image;
					}
				}
            }
        },

        removeImage() {
            this.$confirm({
                title: this.$t('confirm.remove_image.title'),
                text: this.$t('confirm.remove_image.message'),
                type: 'warning'
            }).then(({confirmed}) => {
                if (confirmed) {
                    this.selfUpdating = true;

                    this.data[`${this.prop}_selected`] = undefined;
                    this.data[`${this.prop}_data`] = undefined;
                    this.data[this.prop] = null;

                    if (this.withThumbnail) {
                        this.data[`${this.prop}_thumbnail_selected`] = undefined;
                        this.data[`${this.prop}_thumbnail_data`] = undefined;
                        this.data[`${this.prop}_thumbnail`] = null;
                    }

                    this.displayThumbnail = null;
                    this.displayFile = null;
                    this.displaySource = null;
                }
            });
        }
    }
}
</script>